import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Colors as C } from '../../constants/Colors.js';
import { supabase } from '../../lib/supabase.js';
import { uploadSorteoImagen } from '../../lib/sorteosService.js';
import { DCard, BtnGhost } from '../components/MarketplaceAtoms.js';

const btnMini = {
  padding: '5px 10px',
  borderRadius: 8,
  border: '0.5px solid rgba(200,168,75,0.35)',
  background: 'rgba(200,168,75,0.08)',
  color: C.gold,
  fontSize: 10,
  fontWeight: 600,
  cursor: 'pointer',
};

function toLocalInput(dt) {
  if (!dt) return '';
  const d = new Date(dt);
  if (Number.isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(val) {
  if (!val) return null;
  const d = new Date(val);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function defaultProductoJson() {
  return {
    tipo: 'disco',
    artista: '',
    titulo_disco: '',
    anio: '',
    formato: '',
    condicion: '',
    sello: '',
    notas: '',
  };
}

/** Panel admin gestión sorteos Comunidad. */
export default function AdminSorteosPanel({ user, onError }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const [parts, setParts] = useState([]);
  const [loadingParts, setLoadingParts] = useState(false);
  const imgRef = useRef(null);

  const [form, setForm] = useState(null);
  const loadReq = useRef(0);

  const opcionesInscriptosUnicos = useMemo(() => {
    const seen = new Map();
    const sorted = [...(parts || [])].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    for (const p of sorted) {
      const uid = p.user_id;
      if (!uid || seen.has(uid)) continue;
      seen.set(uid, p.username || String(uid).slice(0, 8));
    }
    return [...seen.entries()].map(([id, label]) => ({ id, label }));
  }, [parts]);

  const loadList = useCallback(async () => {
    const rq = ++loadReq.current;
    setLoading(true);
    onError('');
    try {
      const { data, error } = await supabase
        .from('sorteos')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(100);
      if (rq !== loadReq.current) return;
      if (error) throw error;
      setRows(data || []);
    } catch (e) {
      if (rq !== loadReq.current) return;
      onError(e?.message || 'No se pudieron cargar sorteos');
      setRows([]);
    } finally {
      if (rq === loadReq.current) setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadParticipants = useCallback(
    async sorteoId => {
      setLoadingParts(true);
      try {
        const { data: ins, error } = await supabase
          .from('sorteos_inscripciones')
          .select('id,user_id,grooves_pagados,canal,created_at')
          .eq('sorteo_id', sorteoId)
          .order('created_at', { ascending: false });
        if (error) throw error;
        const list = ins || [];
        const ids = [...new Set(list.map(r => r.user_id).filter(Boolean))];
        let names = {};
        if (ids.length) {
          const { data: profs } = await supabase.from('profiles').select('id,username,email').in('id', ids);
          names = Object.fromEntries((profs || []).map(p => [p.id, p.username || p.email || p.id?.slice(0, 8)]));
        }
        setParts(list.map(row => ({ ...row, username: names[row.user_id] || row.user_id })));
      } catch (e) {
        onError(e?.message || 'No se cargaron inscripciones');
        setParts([]);
      } finally {
        setLoadingParts(false);
      }
    },
    [onError]
  );

  const openRow = useCallback(
    r => {
      setSel(r);
      const pj = typeof r.producto === 'object' && r.producto ? r.producto : {};
      setForm({
        titulo: r.titulo || '',
        descripcion: r.descripcion || '',
        producto: { ...defaultProductoJson(), ...pj },
        imagen_url: r.imagen_url || '',
        video_url: r.video_url || '',
        resultado_video_url: r.resultado_video_url || '',
        fecha_sorteo: toLocalInput(r.fecha_sorteo),
        inscripciones_desde: toLocalInput(r.inscripciones_desde),
        inscripciones_hasta: toLocalInput(r.inscripciones_hasta),
        modalidad_precio: r.modalidad_precio || 'gratis',
        grooves_entrada: Number(r.grooves_entrada) || 0,
        estado: r.estado || 'borrador',
        ganadores_previstos: Number(r.ganadores_previstos) || 1,
        ganador_principal_user_id: r.ganador_principal_user_id || '',
        ganador_suplente_2_user_id: r.ganador_suplente_2_user_id || '',
        ganador_suplente_3_user_id: r.ganador_suplente_3_user_id || '',
        legal_texto: r.legal_texto || '',
        legal_url: r.legal_url || '',
      });
      loadParticipants(r.id);
    },
    [loadParticipants]
  );

  const nuevoBorrador = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    onError('');
    try {
      const now = new Date();
      const hasta = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
      const fechaS = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
      const { data, error } = await supabase
        .from('sorteos')
        .insert({
          titulo: 'Nuevo sorteo',
          descripcion: '',
          producto: defaultProductoJson(),
          fecha_sorteo: fechaS.toISOString(),
          inscripciones_desde: now.toISOString(),
          inscripciones_hasta: hasta.toISOString(),
          modalidad_precio: 'gratis',
          grooves_entrada: 0,
          estado: 'borrador',
          ganadores_previstos: 1,
          created_by: user?.id || null,
        })
        .select('*')
        .single();
      if (error) throw error;
      await loadList();
      if (data) openRow(data);
    } catch (e) {
      onError(e?.message || 'No se pudo crear borrador');
    } finally {
      setBusy(false);
    }
  }, [busy, loadList, onError, openRow, user?.id]);

  const guardar = useCallback(async () => {
    if (!sel || !form || busy) return;
    setBusy(true);
    onError('');
    try {
      const fechaSorteo = fromLocalInput(form.fecha_sorteo);
      const desde = fromLocalInput(form.inscripciones_desde);
      const hasta = fromLocalInput(form.inscripciones_hasta);
      if (!fechaSorteo || !desde || !hasta) {
        onError('Revisa las fechas (son obligatorias).');
        setBusy(false);
        return;
      }
      const g1 = String(form.ganador_principal_user_id || '').trim() || null;
      const g2 = String(form.ganador_suplente_2_user_id || '').trim() || null;
      const g3 = String(form.ganador_suplente_3_user_id || '').trim() || null;
      const usados = [g1, g2, g3].filter(Boolean);
      if (new Set(usados).size !== usados.length) {
        onError('Ganador principal y suplentes deben ser tres personas distintas (cuando están rellenas).');
        setBusy(false);
        return;
      }
      const payload = {
        titulo: String(form.titulo || '').trim() || 'Sorteo sin título',
        descripcion: String(form.descripcion || '').trim() || null,
        producto: { ...defaultProductoJson(), ...(form.producto || {}) },
        imagen_url: form.imagen_url?.trim() || null,
        video_url: form.video_url?.trim() || null,
        resultado_video_url: form.resultado_video_url?.trim() || null,
        fecha_sorteo: fechaSorteo,
        inscripciones_desde: desde,
        inscripciones_hasta: hasta,
        modalidad_precio: form.modalidad_precio === 'grooves' ? 'grooves' : 'gratis',
        grooves_entrada:
          form.modalidad_precio === 'grooves' ? Math.max(0, Number(form.grooves_entrada) || 0) : 0,
        estado: ['borrador', 'publicado', 'cerrado', 'realizado'].includes(form.estado) ? form.estado : 'borrador',
        ganadores_previstos: Math.max(1, Number(form.ganadores_previstos) || 1),
        ganador_principal_user_id: g1,
        ganador_suplente_2_user_id: g2,
        ganador_suplente_3_user_id: g3,
        legal_texto: String(form.legal_texto || '').trim() || null,
        legal_url: String(form.legal_url || '').trim() || null,
      };

      const { error } = await supabase.from('sorteos').update(payload).eq('id', sel.id);
      if (error) throw error;
      await loadList();
      setSel(s => ({ ...s, ...payload }));
    } catch (e) {
      onError(e?.message || 'No se pudo guardar');
    } finally {
      setBusy(false);
    }
  }, [busy, form, loadList, onError, sel]);

  const borrar = useCallback(async () => {
    if (!sel) return;
    if (sel.estado !== 'borrador') {
      onError('Sólo se pueden borrar borradores sin publicar.');
      return;
    }
    if (
      !window.confirm(
        '¿Eliminar permanentemente este sorteo borrador? Si ya hay inscripciones de prueba, fallará por FK.'
      )
    )
      return;
    setBusy(true);
    onError('');
    try {
      const { error } = await supabase.from('sorteos').delete().eq('id', sel.id);
      if (error) throw error;
      setSel(null);
      setForm(null);
      setParts([]);
      await loadList();
    } catch (e) {
      onError(e?.message || 'No se pudo borrar');
    } finally {
      setBusy(false);
    }
  }, [loadList, onError, sel]);

  const subirImagen = useCallback(async () => {
    if (!sel || !imgRef.current?.files?.[0]) return;
    const f = imgRef.current.files[0];
    setBusy(true);
    onError('');
    try {
      const { url, error } = await uploadSorteoImagen(f, sel.id);
      if (error) throw new Error(error);
      imgRef.current.value = '';
      setForm(ff => ({ ...ff, imagen_url: url }));
      const { error: uErr } = await supabase.from('sorteos').update({ imagen_url: url }).eq('id', sel.id);
      if (uErr) throw uErr;
      await loadList();
    } catch (e) {
      onError(e?.message || 'Falló la subida');
    } finally {
      setBusy(false);
    }
  }, [loadList, onError, sel]);

  const estadLabel = useMemo(
    () => ({
      borrador: { t: 'Borrador', c: '#8899aa' },
      publicado: { t: 'Publicado', c: C.green },
      cerrado: { t: 'Cerrado inscripción', c: '#EF9F27' },
      realizado: { t: 'Realizado', c: C.gold },
    }),
    []
  );

  const inp = {
    width: '100%',
    padding: '8px 10px',
    borderRadius: 8,
    border: '0.5px solid rgba(255,255,255,0.12)',
    background: 'rgba(255,255,255,0.05)',
    color: '#fff',
    fontSize: 12,
    fontFamily: 'inherit',
  };

  const label = { fontSize: 10, color: 'rgba(255,255,255,0.42)', marginBottom: 4, display: 'block' };

  return (
    <div>
      <DCard style={{ padding: '14px 16px', marginBottom: 14 }}>
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.52)', margin: '0 0 12px', lineHeight: 1.55 }}>
          Gestión de sorteos publicados en <strong style={{ color: C.gold }}>Comunidad</strong>: premio + imagen +
          fecha del sorteo, ventana de inscripción, gratis o entrada en grooves (descuento vía función segura).
          Métricas de quién entró y si pagó grooves. Un solo premio habitual con cadena{' '}
          <strong style={{ color: C.gold }}>principal → suplente 2 → suplente 3</strong> si hiciera falta localizar al titular.
          Vídeo YouTube y «resultado vídeo» para el extracto público.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <BtnGhost type="button" disabled={busy} style={{ fontSize: 11, width: 'auto' }} onClick={() => loadList()}>
            Recargar tabla
          </BtnGhost>
          <button type="button" disabled={busy} onClick={nuevoBorrador} style={{ ...btnMini, width: 'auto' }}>
            + Nuevo borrador
          </button>
        </div>
      </DCard>

      <div style={{ display: 'grid', gridTemplateColumns: sel ? 'minmax(0,1fr) minmax(360px,1.1fr)' : '1fr', gap: 14 }}>
        <DCard style={{ padding: 0, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 20, fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Cargando…</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 720 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
                    <th style={{ padding: '10px 12px' }}>Sorteo</th>
                    <th style={{ padding: '10px 12px' }}>Estado</th>
                    <th style={{ padding: '10px 12px' }}>Inscripc.</th>
                    <th style={{ padding: '10px 12px' }}>Entrada</th>
                    <th style={{ padding: '10px 12px' }} />
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const st = estadLabel[r.estado] || estadLabel.borrador;
                    return (
                      <tr key={r.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: sel?.id === r.id ? 'rgba(200,168,75,0.06)' : 'transparent' }}>
                        <td style={{ padding: '10px 12px', fontWeight: 650, color: '#fff', maxWidth: 220 }}>
                          {r.titulo}
                          <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 4 }}>{r.id.slice(0, 8)}…</div>
                        </td>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: st.c }}>{st.t}</td>
                        <td style={{ padding: '10px 12px' }}>
                          <span title="Automático tras inscripciones">{r.inscripciones_count ?? 0}</span>
                        </td>
                        <td style={{ padding: '10px 12px', fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                          {r.modalidad_precio === 'grooves' && r.grooves_entrada > 0 ? (
                            <>
                              <span style={{ color: C.gold }}>{r.grooves_entrada}</span> G
                            </>
                          ) : (
                            'Gratis'
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          <button type="button" onClick={() => openRow(r)} style={{ ...btnMini, width: 'auto' }}>
                            Editar / métricas
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {!rows.length ? (
                <div style={{ padding: 16, fontSize: 12, color: 'rgba(255,255,255,0.35)' }}>
                  Todavía no hay sorteos. Creá un borrador.
                </div>
              ) : null}
            </div>
          )}
        </DCard>

        {sel && form ? (
          <DCard style={{ padding: '14px 16px', position: 'sticky', top: 12, maxHeight: 'calc(100vh - 140px)', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <h2 style={{ margin: 0, fontSize: 15, color: C.gold }}>Editar sorteo</h2>
              <button type="button" onClick={() => { setSel(null); setForm(null); setParts([]); }} style={{ ...btnMini, borderColor: 'rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.55)' }}>
                Cerrar
              </button>
            </div>

            <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <label style={label}>Título</label>
                <input value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={label}>Descripción</label>
                <textarea rows={3} value={form.descripcion} onChange={e => setForm(f => ({ ...f, descripcion: e.target.value }))} style={{ ...inp, resize: 'vertical' }} />
              </div>

              <div style={{ fontSize: 10, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.35)', marginTop: 8 }}>PREMIO / PRODUCTO</div>
              {['artista', 'titulo_disco', 'anio', 'formato', 'condicion', 'sello', 'notas'].map(field => (
                <div key={field}>
                  <label style={label}>{field.replace('_', ' ')}</label>
                  <input
                    value={form.producto?.[field] || ''}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        producto: { ...f.producto, [field]: e.target.value },
                      }))
                    }
                    style={inp}
                  />
                </div>
              ))}

              <div style={{ marginTop: 8 }}>
                <label style={label}>Imagen (URL manual o subir archivo)</label>
                <input value={form.imagen_url || ''} onChange={e => setForm(f => ({ ...f, imagen_url: e.target.value }))} style={{ ...inp, marginBottom: 8 }} placeholder="https://..." />
                <input ref={imgRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ fontSize: 11 }} />
                <button type="button" disabled={busy} onClick={() => subirImagen()} style={{ ...btnMini, marginLeft: 8 }}>
                  Subir
                </button>
              </div>

              <div>
                <label style={label}>Vídeo (YouTube o embed)</label>
                <input value={form.video_url} onChange={e => setForm(f => ({ ...f, video_url: e.target.value }))} style={inp} placeholder="https://www.youtube.com/watch?v=…" />
              </div>
              <div>
                <label style={label}>Vídeo resultado (tras sorteo)</label>
                <input
                  value={form.resultado_video_url}
                  onChange={e => setForm(f => ({ ...f, resultado_video_url: e.target.value }))}
                  style={inp}
                  placeholder="Opcional · enlace público"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 10 }}>
                <div>
                  <label style={label}>Fecha hora sorteo</label>
                  <input type="datetime-local" value={form.fecha_sorteo} onChange={e => setForm(f => ({ ...f, fecha_sorteo: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={label}>Inscripciones desde</label>
                  <input type="datetime-local" value={form.inscripciones_desde} onChange={e => setForm(f => ({ ...f, inscripciones_desde: e.target.value }))} style={inp} />
                </div>
                <div>
                  <label style={label}>Inscripciones hasta</label>
                  <input type="datetime-local" value={form.inscripciones_hasta} onChange={e => setForm(f => ({ ...f, inscripciones_hasta: e.target.value }))} style={inp} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={label}>Precio entrada</label>
                  <select
                    value={form.modalidad_precio}
                    onChange={e =>
                      setForm(f => ({
                        ...f,
                        modalidad_precio: e.target.value,
                        grooves_entrada: e.target.value === 'gratis' ? 0 : f.grooves_entrada,
                      }))
                    }
                    style={inp}
                  >
                    <option value="gratis">Gratis</option>
                    <option value="grooves">Grooves</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Grooves por entrada</label>
                  <input
                    type="number"
                    min={0}
                    disabled={form.modalidad_precio !== 'grooves'}
                    value={form.grooves_entrada}
                    onChange={e => setForm(f => ({ ...f, grooves_entrada: Math.max(0, Number(e.target.value) || 0) }))}
                    style={inp}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={label}>Estado</label>
                  <select value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value }))} style={inp}>
                    <option value="borrador">Borrador (no muestra público hasta publicar)</option>
                    <option value="publicado">Publicado · inscripción según fechas</option>
                    <option value="cerrado">Cerrado (manual; sigue visible Comunidad)</option>
                    <option value="realizado">Realizado</option>
                  </select>
                </div>
                <div>
                  <label style={label}>Ganadores previstos · premios a entregar (informativo)</label>
                  <input
                    type="number"
                    min={1}
                    value={form.ganadores_previstos}
                    onChange={e => setForm(f => ({ ...f, ganadores_previstos: Math.max(1, Number(e.target.value) || 1) }))}
                    style={inp}
                  />
                  <p style={{ fontSize: 9, color: 'rgba(255,255,255,0.38)', margin: '6px 0 0', lineHeight: 1.45 }}>
                    Usualmente «1»: un único disco/premio, con orden de llamada ganador principal y hasta dos suplentes abajo (elige entre inscriptos cuando cierres el sorteo).
                  </p>
                </div>
              </div>

              <div
                style={{
                  marginTop: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  background: 'rgba(200,168,75,0.06)',
                  border: '1px dashed rgba(200,168,75,0.28)',
                }}
              >
                <div style={{ fontSize: 10, letterSpacing: '0.1em', color: C.gold, marginBottom: 10 }}>
                  CADENA DE GANADORÍA (tras el sorteo)
                </div>
                <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', margin: '0 0 12px', lineHeight: 1.5 }}>
                  1) Ganador principal — 2) Si no localiza o no responde, suplente 2 — 3) Suplente 3. Elegí entre quienes se inscribieron (recargás la ficha después de tener inscriptos).
                </p>
                {[
                  ['ganador_principal_user_id', '1 · Ganador principal'],
                  ['ganador_suplente_2_user_id', '2 · Suplente (si falla el principal)'],
                  ['ganador_suplente_3_user_id', '3 · Suplente (si falla el 2º)'],
                ].map(([field, etiqueta]) => (
                  <div key={field} style={{ marginBottom: 10 }}>
                    <label style={label}>{etiqueta}</label>
                    <select
                      value={form[field] || ''}
                      onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))}
                      style={inp}
                    >
                      <option value="">— Sin asignar —</option>
                      {opcionesInscriptosUnicos.map(opt => (
                        <option key={opt.id} value={opt.id}>
                          @{opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
                {!opcionesInscriptosUnicos.length ? (
                  <p style={{ fontSize: 10, color: 'rgba(255,255,255,0.32)', margin: 0 }}>
                    Todavía no hay inscriptos; guardá cuando haya lista o asigná tras cerrar plazos.
                  </p>
                ) : null}
              </div>

              <div>
                <label style={label}>Bases legales (texto breve visible en Comunidad)</label>
                <textarea rows={2} value={form.legal_texto} onChange={e => setForm(f => ({ ...f, legal_texto: e.target.value }))} style={{ ...inp, resize: 'vertical' }} placeholder="Enlaces oficiales, RGPD…" />
              </div>
              <div>
                <label style={label}>URL bases / reglamento externo</label>
                <input value={form.legal_url} onChange={e => setForm(f => ({ ...f, legal_url: e.target.value }))} style={inp} placeholder="https://..." />
              </div>

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                <button type="button" disabled={busy} onClick={guardar} style={{ ...btnMini, padding: '8px 16px', borderColor: 'rgba(29,158,117,0.45)', color: C.green }}>
                  Guardar cambios
                </button>
                {sel.estado === 'borrador' ? (
                  <button type="button" disabled={busy} onClick={borrar} style={{ ...btnMini, borderColor: 'rgba(226,75,74,0.45)', color: C.danger }}>
                    Eliminar borrador
                  </button>
                ) : null}
              </div>
            </div>

            <div style={{ marginTop: 22, paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ fontSize: 11, letterSpacing: '0.1em', color: 'rgba(255,255,255,0.35)', marginBottom: 10 }}>INSCRIPTOS ({parts.length})</div>
              {loadingParts ? (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>Cargando…</div>
              ) : parts.length === 0 ? (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Nadie se ha inscrito aún.</div>
              ) : (
                <div style={{ overflowX: 'auto', maxHeight: 240 }}>
                  <table style={{ width: '100%', fontSize: 10, borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ color: 'rgba(255,255,255,0.42)', borderBottom: '1px solid rgba(255,255,255,0.08)', textAlign: 'left' }}>
                        <th style={{ padding: 6 }}>Usuario</th>
                        <th style={{ padding: 6 }}>Fecha</th>
                        <th style={{ padding: 6 }}>Grooves</th>
                        <th style={{ padding: 6 }}>Canal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {parts.map(p => (
                        <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', color: '#ddd' }}>
                          <td style={{ padding: '6px 8px' }}>{p.username}</td>
                          <td style={{ padding: '6px 8px', whiteSpace: 'nowrap', color: 'rgba(255,255,255,0.55)' }}>
                            {new Date(p.created_at).toLocaleString('es-ES')}
                          </td>
                          <td style={{ padding: '6px 8px' }}>{p.grooves_pagados || '—'}</td>
                          <td style={{ padding: '6px 8px' }}>{p.canal}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </DCard>
        ) : null}
      </div>
    </div>
  );
}
